import React, {Fragment, useCallback, useContext, useEffect, useRef, useState} from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import Price, { CURRENCY } from '../../components/Price';
import QRCode from '../../components/QRCode';
import { UserContext } from '../../store/UserProvider';
import Alert, { ALERT_TYPE } from '../../components/Alert';
import { ROUTES } from '../../defs/routes';
import Service from '../../utils/service';
import DateFormat from '../../components/DateFormat';
import log from '../../utils/logger';
import { RepositoryContext } from '../../store/RepositoryProvider';
import API from '../../server/api';
import DateTZ from '../../models/date/DateTZ';
import ReportFilterPaginate, { SHOW_MODE } from '../../components/ReportFilterPaginate';
import Table, { ALIGN_TABLE } from '../../components/Table';
import InfoDescription from '../../components/InfoDescription';
import Badge, { BADGE_TYPE } from '../../components/Badge';
import {APICode, OrderStatus, PaymentMethod} from '../../models/types';
import Button, { BUTTON_POSITION } from '../../components/Button';

const HandCash = ({ onClose, amountMax }) => {
    const { t } = useTranslation(['common']);

    const navigate = useNavigate();

    const userInfo = useContext(UserContext);
    const repository = useContext(RepositoryContext);

    const [cash, setCash] = useState(0);
    const [query, setQuery] = useState('');
    const [selectedOrders, setSelectedOrders] = useState({}); // Хранилище выбранных заказов
    const statusService = useRef(null);
    const collections = useRef([]);
    const newCollections = useRef([]);

    const [error, setError] = useState('');
    const [collectionId, setCollectionId] = useState(null);

    const [isLoading, setIsLoading] = useState(false);

    const ORDER_STATUS = {
        paid: BADGE_TYPE.SUCCESS,
        awaiting_payment: BADGE_TYPE.WARN,
        cancelled: BADGE_TYPE.ERROR,
        completed: BADGE_TYPE.INFO,
        awaiting_assignment: BADGE_TYPE.INFO
    };

    useEffect(() => {
        userInfo.getUser().then((u) => {
            setQuery(u.id);
        }).catch(() => {
            navigate(ROUTES.AUTH, { replace: true });
        });
    }, []);

    const getCash = () => {
        let money = parseFloat(cash);
        return cash && collectionId ? JSON.stringify({ query, cash: money, id: collectionId, currency: CURRENCY.RUB }) : '';
    };

    const clearNewCollection = (collection) => {
        let idx = newCollections.current.findIndex(c => c.day === collection.day && c.time === collection.time);
        if (idx >= 0) {
            newCollections.current.splice(idx, 1);
        }
    };

    const handleCreateCollection = useCallback(async () => {

        const resultOrders = Object.entries(selectedOrders).map(([orderId, data]) => ({
            order: orderId,
            amount: data.amount,
            salary: data.salary
        }));

        const data = {
            amount: cash,
            orders: resultOrders
        }

        if (!error && cash > 0) {
            setIsLoading(true);

            repository.action(API.cashier.createCollection, data).then((cashResult) => {

                if (cashResult?._id) {
                    setCollectionId(cashResult?._id);
                }

                setIsLoading(false);
            }).catch((error) => {
                log.error(error);
                setIsLoading(false);
                if (error.error === APICode.TOO_MANY_REQUESTS) {
                    setError(t('cashbox:total_salary.error.too_many_requests'));
                } else {
                    setError(t('cashbox:total_salary.error.fail'));
                }
            });
        } else {
            setIsLoading(false);
        }

        setError(null);
    }, [error, cash]);

    const setOrder = (isCheck, order) => {
        let salary = order.cashierRewards.reduce((sum, item) => sum + item.amount, 0);
        let amount = (order.paymentDetails.method === PaymentMethod.CASH
            ? (isCheck ? order.paymentDetails.partialValue : -order.paymentDetails.partialValue)
            : 0);

        let totalAmount = (order.paymentDetails.method === PaymentMethod.CASH
            ? (isCheck ? amount - salary : amount + salary)
            : (isCheck ? -salary : salary));

        setCash(cash + totalAmount);

        setSelectedOrders(prev => {
            const newState = { ...prev };
            if (isCheck) {
                newState[order.id] = {
                    amount: order.paymentDetails.partialValue,
                    salary: salary
                };
            } else {
                delete newState[order.id];
            }
            return newState;
        });
    };

    const updateAmounts = (order, amount) => {

        setSelectedOrders(prev => {
            const newState = { ...prev };

            if (newState[order.id]) {
                newState[order.id].amount = amount || 0;
            }
            return newState;
        });

       setTimeout(() => {
           const totalAmount = Object.values(selectedOrders)?.reduce((sum, item) => sum + item.amount, 0) || 0;
           setCash(totalAmount || 0);
       }, 100)


    }

    // update selected orders and set new cash

    const updateHistory = () => {
        const range = {
            fromDate: DateFormat.Request({ date: DateTZ.add(-1, 'day') }),
            toDate: DateFormat.Request({ date: DateTZ.add(1, 'day') })
        };
        repository.action(API.cashier.cashbox, range).then((cashbox) => {
            newCollections.current = cashbox.history.filter((h) =>
                !collections.current.find((c) => h.day === c.day && h.time === c.time) ||
                newCollections.current.find((c) => h.day === c.day && h.time === c.time)
            );
            collections.current = cashbox.history;
        }).catch((e) => {
            log.error(e);
            collections.current = [];
        });
    };

    useEffect(() => {
        if (cash > 0 && cash > amountMax) {
            setError(t('cashbox:total_cash.over_limit'));
        } else {
            setError('');
        }
    }, [cash]);

    useEffect(() => {
        const range = {
            fromDate: DateFormat.Request({ date: DateTZ.add(-1, 'day') }),
            toDate: DateFormat.Request({ date: DateTZ.add(1, 'day') })
        };

        let service = new Service(() => {
            updateHistory();
        }, 20000);

        repository.action(API.cashier.cashbox, range).then((cashbox) => {
            collections.current = cashbox.history;

            service.start(20000);

            statusService.current = service;
        }).catch((e) => {
            log.error(e);
            collections.current = [];
        });

        return () => {
            statusService.current && statusService.current.stop();
            service.stop();
        };
    }, []);

    const handleClose = useCallback(() => {
        typeof onClose === 'function' && onClose();
    }, []);

    return (
        <div className="trip-app">
            <div className="trip-app-body">
                {error && (
                    <Alert type={ALERT_TYPE.ERROR} dismissible={true}>{error}</Alert>
                )}

                {collectionId && (
                   <>
                       <Alert type={ALERT_TYPE.SUCCESS} dismissible={false}>
                           {t('collection:collection.complete')}
                       </Alert>
                       <Button
                           className="mb-5"
                           onClick={() => handleClose()}
                           position={BUTTON_POSITION.CENTER}
                       >
                           {t('cashbox:button.close')}
                       </Button>
                   </>
                )}

                {!collectionId && (
                    <>
                        <div className="my-available-orders">
                            <ReportFilterPaginate
                                apiHandler={(reportParams) => {
                                    return userInfo.getMyUncollectedOrders(reportParams);
                                }}
                                show={SHOW_MODE.ALWAYS}
                                sort={(a, b) => a.date < b.date ? 1 : (a.date > b.date ? -1 : 0)}
                                placeholder={t('sale:my_sales.filter')}
                                output={(list) => (
                                    <Table cols={2} className="my-available-orders_table">
                                        {list.map((order) => (
                                            <Fragment key={order.id}>

                                                <Table.Row>
                                                    <Table.Cell>
                                                        <div className={`checkbox my-available-orders_checkbox}`}>
                                                            <div>
                                                                <input type="checkbox"
                                                                       value={order.id}
                                                                       id={`f${order.id}`}
                                                                       defaultChecked={!!selectedOrders[order.id]}
                                                                       onChange={(e) => setOrder(e.target.checked, order)}
                                                                />
                                                                <label htmlFor={`f${order.id}`} className="mb-1">
                                                                    <div className="d-flex gap-1 ms-4 position-relative" style={{top: "-3px"}}>
                                                                        <Badge type={BADGE_TYPE.INFO}>{t(`common:payment_method.${order.paymentDetails.method}`)}</Badge>
                                                                        <Badge type={ORDER_STATUS[order.status]}>{t(`sale:status.${order.status}`)}</Badge>
                                                                    </div>
                                                                </label>
                                                            </div>
                                                            <InfoDescription description={
                                                                <>
                                                                    {order?.schedule?.number || ''} - {order?.schedule?.date ? <DateFormat.DateTimeSmall date={order.schedule.date} tz={true}/> : ''}<div>{order.excursion.title}</div>
                                                                </>
                                                            }>
                                                                <div>
                                                                    {t('sale:my_sales.order', { order: order.number })} - {t('sale:my_sales.from_date')}<DateFormat.DateTimeSmall date={order.date} tz={true}/>
                                                                </div>
                                                            </InfoDescription>
                                                        </div>
                                                    </Table.Cell>
                                                    <Table.Cell align={ALIGN_TABLE.RIGHT}>
                                                        <div className={"pt-1"}>
                                                            {!!selectedOrders[order.id] && (
                                                                <div className="d-flex flex-column align-items-end">
                                                                    <input
                                                                        type="number"
                                                                        className={`form-control`}
                                                                        style={{width: "100px", textAlign: "right"}}
                                                                        min={0}
                                                                        max={selectedOrders[order.id].amount}
                                                                        onInput={(e) => updateAmounts(order, parseFloat(e.target.value))}
                                                                        onChange={(e) => updateAmounts(order, parseFloat(e.target.value))}
                                                                        defaultValue={order.paymentDetails.method ===  PaymentMethod.CASH ? selectedOrders[order.id].amount-selectedOrders[order.id].salary : -selectedOrders[order.id].salary}
                                                                    />
                                                                    <div>
                                                                        ЗП: <Price currency={CURRENCY.RUB} value={selectedOrders[order.id].salary} />
                                                                    </div>
                                                                    <div>
                                                                        Опл. нал: <Price currency={CURRENCY.RUB} value={order.paymentDetails.method ===  PaymentMethod.CASH ? order.paymentDetails.amount : 0} />
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </Table.Cell>
                                                </Table.Row>
                                                <Table.Row delim={true} className="mb-1" />
                                            </Fragment>
                                        ))}
                                    </Table>
                                )}
                                filters={[]}
                            />
                        </div>
                        <hr className="mb-3" />
                        <div>
                            <Button.Panel className="mb-0 mt-0 pt-3">
                                <InfoDescription description={t('cashbox:total_cash.summary_amount')}>
                                    <input
                                        type="number"
                                        className={`form-control`}
                                        style={{width: "100px", textAlign: "right"}}
                                        min={0}
                                        onChange={(e) => setCash(parseFloat(e.target.value) || 0)}
                                        value={cash}
                                    />
                                </InfoDescription>
                                <Button
                                    position={BUTTON_POSITION.RIGHT}
                                    onClick={() => handleCreateCollection()}
                                    disabled={cash <= 0 || !!error || isLoading}
                                >
                                    {t('cashbox:button.hand_cash')}
                                </Button>
                            </Button.Panel>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

HandCash.propTypes = {
    amountMax: PropTypes.number.isRequired,
    onCancel: PropTypes.func,
};

export default HandCash;